import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useCopy, useFetch } from '@/app/hooks';
import { useStoreApi } from '@/app/providers/store';
import { useRouter } from '@/i18n/routing';
import { clawsDeleteSession, clawsGetChatHistory, clawsShareSession, clawsUpdateSession } from '../apis';
import type { ChatMessageItem } from '../types';

interface RenameSessionParams {
  sessionId: string;
  name: string;
}

interface ShareSessionParams {
  sessionId: string;
  messageIds?: string[];
}

export const useSessionActions = () => {
  const storeApi = useStoreApi();
  const router = useRouter();
  const t = useTranslations();
  const { copyAsync } = useCopy();

  const { loading: renamingSession, runAsync: renameSessionAsync } = useFetch(
    ({ sessionId, name }: RenameSessionParams) =>
      clawsUpdateSession({
        body: {
          session_id: sessionId,
          name,
        },
      }),
    {
      manual: true,
    },
  );

  const { loading: deletingSession, runAsync: deleteSessionAsync } = useFetch(
    (sessionId: string) =>
      clawsDeleteSession({
        path: {
          session_id: sessionId,
        },
      }),
    {
      manual: true,
    },
  );

  const { loading: sharingSession, runAsync: shareSessionAsync } = useFetch(clawsShareSession, {
    manual: true,
  });

  const { loading: syncingShareIds, runAsync: fetchAssistantHistory } = useFetch(
    (sessionId: string) =>
      clawsGetChatHistory({
        query: { session_id: sessionId, 'pager.page': 0, 'pager.pagesz': 100, role: ['assistant', 'spawn', 'cron'] },
      }),
    { manual: true },
  );

  const syncShareMessageIds = async (
    sessionId: string,
    currentMessages: ChatMessageItem[],
    setMessages: Dispatch<SetStateAction<ChatMessageItem[]>>,
  ): Promise<string[]> => {
    const { data, error } = await fetchAssistantHistory(sessionId);
    if (error || !data?.messages) return [];

    // SSE 下发的 message_id 是 UUID，分享接口要求的是历史接口的 snowflake id，必须用历史回填覆盖。
    // 合并气泡可能跨多条 stream_seq：
    // - 有 turn_id 的消息按 turn_id 聚合该 turn 内所有数字 id（一个 turn = 正文 + tool + artifact）；
    // - 没有 turn_id 的消息（message 工具直发、cron/spawn 产物）按各自 stream_seq 单条回填。
    const streamSeqToTurnId = new Map<string, string>();
    const turnIdToServerIds = new Map<string, string[]>();
    const streamSeqToServerId = new Map<string, string>();
    for (const msg of data.messages) {
      if (!msg.id) continue;
      if (msg.stream_seq) {
        streamSeqToServerId.set(msg.stream_seq, msg.id);
        if (msg.turn_id) streamSeqToTurnId.set(msg.stream_seq, msg.turn_id);
      }
      if (!msg.turn_id) continue;
      const list = turnIdToServerIds.get(msg.turn_id) ?? [];
      list.push(msg.id);
      turnIdToServerIds.set(msg.turn_id, list);
    }

    const applyServerIds = (list: ChatMessageItem[]) => {
      let mutated = false;
      const next = list.map((msg) => {
        if (msg.role !== 'assistant') return msg;
        const seqs = msg.streamSeqs ?? [];
        if (!seqs.length) return msg;
        const serverIds: string[] = [];
        for (const seq of seqs) {
          const turnId = streamSeqToTurnId.get(seq);
          if (turnId) {
            const ids = turnIdToServerIds.get(turnId);
            if (ids?.length) serverIds.push(...ids);
          } else {
            const id = streamSeqToServerId.get(seq);
            if (id) serverIds.push(id);
          }
        }
        const uniqueIds = Array.from(new Set(serverIds));
        if (!uniqueIds.length) return msg;
        mutated = true;
        return { ...msg, serverMessageId: uniqueIds[0], shareMessageIds: uniqueIds };
      });
      return { mutated, next };
    };

    const applied = applyServerIds(currentMessages).next;
    const selectableIds = Array.from(
      new Set(
        applied.flatMap(
          (message) => message.shareMessageIds ?? (message.serverMessageId ? [message.serverMessageId] : []),
        ),
      ),
    );

    setMessages((prev) => {
      const { mutated, next } = applyServerIds(prev);
      return mutated ? next : prev;
    });

    return selectableIds;
  };

  const renameSession = async ({ sessionId, name }: RenameSessionParams) => {
    const response = await renameSessionAsync({ sessionId, name });
    if (response.error) {
      return response;
    }

    const { activeClaw, sessions, setSessions } = storeApi.getState();
    if (activeClaw?.id) {
      const updatedSessions = sessions.map((session) => {
        if (session.id !== sessionId) return session;
        return {
          ...session,
          name,
        };
      });
      setSessions(activeClaw, updatedSessions);
    }

    return response;
  };

  const deleteSession = async (sessionId: string) => {
    const response = await deleteSessionAsync(sessionId);
    if (response.error) {
      return response;
    }

    const { activeClaw, sessions, activeSessionId, setSessions, setActiveSessionId } = storeApi.getState();
    if (activeClaw?.id) {
      const nextSessions = sessions.filter((session) => session.id !== sessionId);
      setSessions(activeClaw, nextSessions);
    }
    if (activeSessionId === sessionId) {
      setActiveSessionId(null);
      router.push('/claw');
    }

    return response;
  };

  const shareSession = async ({ sessionId, messageIds }: ShareSessionParams) => {
    if (!sessionId || sharingSession) return;

    const getShareUrlPromise = shareSessionAsync({
      body: {
        session_id: sessionId,
        ...(messageIds?.length ? { message_ids: messageIds } : {}),
      },
    }).then((result) => {
      if (result.error || !result.data?.session_id) {
        const message = result.error?.message ?? '';
        const errorKey = message.includes('message count')
          ? 'claw.chat.share.errorMessageTooMany'
          : message.includes('total file size')
            ? 'claw.chat.share.errorFileTooLarge'
            : 'claw.chat.share.error';
        toast({
          type: 'error',
          message: t(errorKey),
        });
        return '';
      }
      return `${window.location.origin}/claw/share-session/${result.data.session_id}`;
    });

    await copyAsync(getShareUrlPromise, {
      copySuccessMessage: t('claw.chat.share.success'),
      copyFailedMessage: t('claw.chat.share.failed'),
    });
  };

  return {
    renamingSession,
    deletingSession,
    sharingSession,
    syncingShareIds,
    renameSession,
    deleteSession,
    shareSession,
    syncShareMessageIds,
  };
};
